import { Close, MoreEllipsisHorizontal } from "@dreamhub/designsystem/icons";
import LayoutTabs from "@/components/controls/LayoutTabs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { EmptyFilters, Filters, FiltersSetter } from "@/services/api/types";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import {
  useDeleteViewMutation,
  useUpdateViewsMutation,
  useViewsAll
} from "@/entities/views/hooks";
import { createDataMapById } from "@/utils/datamaps";
import toast from "react-hot-toast";
import NewViewsSelection from "@/components/layouts/ViewsSelection/NewViewsSelection";
import {
  sortQueryToState,
  normalizeFiltersAfterDeserialization,
  resolveCurrentUserFilters
} from "@/entities/views/helpers";
import { useUsersMe } from "@/entities/users/hooks";
import { markViewAppliedFilters } from "@/components/controls/ToolbarActions/utils";
import { SortingState, Updater } from "@tanstack/react-table";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import ColumnsManagement from "@/components/layouts/ColumnsManagement/ColumnsManagement";
import DealsColumnsManagement from "@/pages/deals/components/DealsColumnsManagement";
import { ColumnData } from "@/entities/dataTables/types";
import {
  applyTempViewsToServerViews,
  deleteTempView,
  recordActivatedViewConfig,
  saveSelectedViewId,
  getSelectedViewId,
  clearSelectedViewId
} from "@/entities/dataTables/helpers";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { cn } from "@/utils/utils";
import { APIFieldItem } from "@/pages/settings/types";
import {
  find,
  forEach,
  isArray,
  isEmpty,
  isNull,
  map,
  mapValues,
  sum
} from "lodash";

export const USER_SORT_BY = "myOrder";

const showSaveErrorToast = (t: TFunction) =>
  toast.error(
    <ToastBody
      title={t("datatoolbar.views.errorSaving")}
      actions={
        <IconButton
          size="md"
          variant="iconNoBg"
          iconColor="secondary"
          onClick={() => toast.dismiss()}
        >
          <Close />
        </IconButton>
      }
    />,
    {
      icon: null
    }
  );

export const getNumberOfActiveFilters = (
  filters: Filters,
  hiddenColumns?: string[]
) =>
  sum(
    map(filters, (value, key) =>
      !hiddenColumns?.includes(key) &&
      ((isArray(value) && !isEmpty(value)) ||
        (!isArray(value) && !isNull(value)))
        ? 1
        : 0
    )
  );

export interface DataTableToolbarImplProps {
  viewType: ViewType;
  selectedView: ViewDataConfiguration;
  setSelectedView: React.Dispatch<React.SetStateAction<ViewDataConfiguration>>;
  filters: Filters;
  setFilters: FiltersSetter;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  groupBy: string[];
  setGroupBy: (groupBy: string[]) => void;
  columnsState: ColumnData[];
  setColumnsState: React.Dispatch<React.SetStateAction<ColumnData[]>>;
  sorting: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;
  defaultColumnsState: ColumnData[];
}

interface DataTableToolbarProps extends DataTableToolbarImplProps {
  filterOptions: DataTableFilter[];
  sortByOptions: string[];
  defaultSortByValue: string;
  tableId: string;
  groupByOptions?: string[];
  shouldShowEditColumns?: boolean;
  onColumnsChange?: (updatedColumns: ColumnData[]) => void;
  className?: string;
  actionsClassName?: string;
  customFields?: APIFieldItem[];
  onViewActivated?: () => void;
}

const DataTableToolbar: React.FC<DataTableToolbarProps> = ({
  viewType,
  selectedView,
  setSelectedView,
  setFilters,
  setSearchTerm,
  onSortingChange,
  defaultColumnsState,
  defaultSortByValue,
  setGroupBy,
  setColumnsState,
  tableId,
  onColumnsChange,
  className,
  customFields,
  onViewActivated
}) => {
  const { t, i18n } = useTranslation();
  const [showViewSelection, setShowViewSelection] = useState(false);
  const [views, setViews] = useState<ViewDataConfiguration[]>([]);
  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(false);
  const hasAttemptedRestoreRef = useRef(false);

  const { views: loadedViews, refetch } = useViewsAll(viewType);
  const currentUserId = useUsersMe().data?.id;

  useEffect(() => {
    setViews([]);
    setSelectedView(EmptyView(viewType));
    refetch();
  }, [viewType]);

  const activateView = useCallback(
    (newView: ViewDataConfiguration) => {
      if (newView) {
        newView.isOpen = true;
        recordActivatedViewConfig(newView);
        setSelectedView(newView);
        if (newView.id) {
          saveSelectedViewId(viewType, newView.id);
        }

        const normalizedFilters = normalizeFiltersAfterDeserialization(
          resolveCurrentUserFilters(
            newView.config?.filters ?? EmptyFilters,
            currentUserId
          )
        );
        setFilters(markViewAppliedFilters(normalizedFilters));

        onSortingChange(
          sortQueryToState(newView.config?.sort, defaultSortByValue)
        );
        setSearchTerm(newView.config?.searchTerm || "");
        setGroupBy(newView.config?.groupBy ?? []);
        setColumnsState(newView.config?.columns ?? []);
        onViewActivated?.();
      }
    },
    [
      onViewActivated,
      setSelectedView,
      setFilters,
      onSortingChange,
      defaultSortByValue,
      setSearchTerm,
      setGroupBy,
      setColumnsState,
      viewType,
      currentUserId
    ]
  );

  useEffect(() => {
    if (loadedViews?.length > 0) {
      const tempViews = applyTempViewsToServerViews(loadedViews);
      let typeViews = tempViews.filter((view) => view.type === viewType);
      const selectedViewInList = typeViews.find(
        (v) => v.id === selectedView.id
      );
      if (selectedView.id && !selectedViewInList) {
        typeViews = [...typeViews, { ...selectedView, isOpen: true }];
      } else if (
        selectedViewInList &&
        !selectedViewInList.isOpen &&
        selectedView.isOpen
      ) {
        typeViews = typeViews.map((v) =>
          v.id === selectedView.id ? { ...v, isOpen: true } : v
        );
      }

      setViews(typeViews);

      if (views.length === 0) {
        const savedViewId = getSelectedViewId(viewType);
        const savedView = savedViewId
          ? find(typeViews, { id: savedViewId })
          : undefined;

        if (savedView) {
          activateView(savedView);
          return;
        }

        const selectedViewOpen = find(typeViews, {
          id: selectedView.id,
          isOpen: true
        });

        // If selected view isn't open, find first open view or fallback to first view
        if (!selectedViewOpen) {
          const firstOpenView = find(typeViews, { isOpen: true });
          activateView(firstOpenView || typeViews[0] || EmptyView(viewType));
        }
      }
    }
  }, [
    loadedViews,
    views.length,
    selectedView.id,
    selectedView.isOpen,
    activateView,
    viewType
  ]);

  // Restore saved view when selectedView is empty but views are already loaded
  // This handles the case when switching between layouts (e.g., kanban to table)
  useEffect(() => {
    if (hasAttemptedRestoreRef.current) return;

    if (loadedViews?.length > 0 && views.length > 0 && !selectedView.id) {
      hasAttemptedRestoreRef.current = true;

      const savedViewId = getSelectedViewId(viewType);
      const savedView = savedViewId
        ? find(views, { id: savedViewId })
        : undefined;

      if (savedView) {
        activateView(savedView);
      } else {
        // Fallback to first open view or first view
        const firstOpenView = find(views, { isOpen: true });
        activateView(firstOpenView || views[0] || EmptyView(viewType));
      }
    }
  }, [loadedViews?.length, views, selectedView.id, activateView, viewType]);

  useEffect(() => {
    hasAttemptedRestoreRef.current = false;
  }, [viewType]);

  const { mutate: updateViews } = useUpdateViewsMutation();
  const setAndSaveViews = useCallback(
    async (
      newViews: ViewDataConfiguration[],
      oldViews: ViewDataConfiguration[]
    ) => {
      setViews(newViews);

      const removedViews = oldViews.filter(
        (oldView) => !newViews.some((newView) => newView.id === oldView.id)
      );

      forEach(removedViews, (removedView) => {
        deleteTempView(removedView);
        if (removedView.id === selectedView.id) {
          clearSelectedViewId(viewType);
        }
      });

      // Handle when the currently selected view is being closed (not deleted)
      const currentViewInNewViews = newViews.find(
        (v) => v.id === selectedView.id
      );
      if (currentViewInNewViews && !currentViewInNewViews.isOpen) {
        // Find the index of the current view in the old views
        const currentIndex = oldViews.findIndex(
          (v) => v.id === selectedView.id
        );
        const openViews = newViews.filter((v) => v.isOpen);

        if (openViews.length > 0) {
          // Find the previous open view (to the left) by checking views before currentIndex
          let previousOpenView: ViewDataConfiguration | undefined;
          for (let i = currentIndex - 1; i >= 0; i--) {
            const viewAtIndex = newViews.find(
              (v) => v.id === oldViews[i]?.id && v.isOpen
            );
            if (viewAtIndex) {
              previousOpenView = viewAtIndex;
              break;
            }
          }

          // If no previous open view found, use the first open view
          const viewToActivate = previousOpenView || openViews[0];
          activateView(viewToActivate);
        }
      }

      const result = await updateViews({
        views: newViews,
        previousViews: oldViews,
        type: viewType
      });

      if (result.error) {
        showSaveErrorToast(t);
      }
    },
    [selectedView.id, viewType, activateView]
  );

  const deleteViewMutation = useDeleteViewMutation();
  const deleteViewMutationRef = useRef(deleteViewMutation);
  deleteViewMutationRef.current = deleteViewMutation;

  // Deleting a view persists immediately (its own DELETE call), independent of
  // the drawer's Apply diff — otherwise a background views refetch can revert
  // the pending removal before Apply runs, and the delete silently no-ops.
  const handleDeleteView = useCallback(
    async (viewToDelete: ViewDataConfiguration): Promise<boolean> => {
      const remainingViews = views.filter((v) => v.id !== viewToDelete.id);
      setViews(remainingViews);
      deleteTempView(viewToDelete);

      const wasSelectedView = viewToDelete.id === selectedView.id;
      if (wasSelectedView) {
        clearSelectedViewId(viewType);
        const firstOpenView = find(remainingViews, { isOpen: true });
        activateView(
          firstOpenView || remainingViews[0] || EmptyView(viewType)
        );
      }

      try {
        await deleteViewMutationRef.current.mutateAsync({
          pathParams: { viewId: viewToDelete.id }
        });
        return true;
      } catch (error) {
        // The delete failed, so the view still exists — reactivate it to undo
        // the optimistic switch to the fallback view (and its filters/sort/columns).
        if (wasSelectedView) {
          activateView(viewToDelete);
        }
        showSaveErrorToast(t);
        return false;
      } finally {
        // Reconcile the tab bar with the server either way (restores the row on
        // failure, confirms removal on success).
        await refetch();
      }
    },
    [views, selectedView.id, viewType, activateView, refetch, t]
  );

  const viewsDataMap = useMemo(() => {
    const openViews = views.filter((view) => view.isOpen);
    if (
      selectedView.id &&
      selectedView.isOpen &&
      !openViews.find((v) => v.id === selectedView.id)
    ) {
      openViews.push(selectedView);
    }
    return createDataMapById(openViews);
  }, [views, viewType, selectedView.id, selectedView.isOpen]);
  const viewsToOptions = useMemo(
    () =>
      mapValues(viewsDataMap, (view) => {
        const key = `datatoolbar.presets.${view.name}`;
        return i18n.exists(key) ? t(key) : view.name;
      }),
    [viewsDataMap]
  );

  const viewOrder = useMemo(() => {
    const openViewIds = views
      .filter((view) => view.isOpen)
      .map((view) => view.id);
    if (
      selectedView.id &&
      selectedView.isOpen &&
      !openViewIds.includes(selectedView.id)
    ) {
      openViewIds.push(selectedView.id);
    }
    return openViewIds;
  }, [views, selectedView.id, selectedView.isOpen]);

  return (
    <>
      <div
        className={cn(
          "flex gap-2 border-b border-border-border1 justify-between pr-4",
          className
        )}
      >
        <div className="flex flow-row items-center gap-1 min-w-0 flex-1 overflow-hidden">
          <LayoutTabs
            defaultValue={selectedView.id}
            options={viewsToOptions}
            order={viewOrder}
            valueSelected={(id) => activateView(viewsDataMap[id])}
            className="border-0"
            enableDragDrop={true}
            onOrderChange={(newOrder) => {
              const reorderedViews = newOrder.map((id) => viewsDataMap[id]);
              const closedViews = views.filter((v) => !v.isOpen);
              const updatedViews = [...reorderedViews, ...closedViews];
              setAndSaveViews(updatedViews, updatedViews);
            }}
            tabClassName="mt-[10px]"
          />
          <IconButton
            variant="iconNoBg"
            data-test="create-new-view"
            iconColor="secondary"
            size="md"
            className="mb-0"
            onClick={() => setShowViewSelection(!showViewSelection)}
            tooltipText={t("datatoolbar.views.slicers.all")}
          >
            <MoreEllipsisHorizontal />
          </IconButton>
        </div>
      </div>
      <NewViewsSelection
        isOpen={showViewSelection}
        onClose={() => setShowViewSelection(false)}
        selectedViews={views}
        onApply={setAndSaveViews}
        onDeleteView={handleDeleteView}
      />
      {viewType === ViewType.DEALS_KANBAN ||
      viewType === ViewType.DEALS_TABLE ? (
        <DealsColumnsManagement
          isOpen={isEditColumnsOpen}
          selectedView={selectedView}
          defaultColumnsState={defaultColumnsState}
          onClose={() => setIsEditColumnsOpen(false)}
          onApply={(updatedColumns) => onColumnsChange?.(updatedColumns)}
          tableId={tableId}
          customFields={customFields}
        />
      ) : (
        <ColumnsManagement
          isOpen={isEditColumnsOpen}
          selectedView={selectedView}
          defaultColumnsState={defaultColumnsState}
          onClose={() => setIsEditColumnsOpen(false)}
          onApply={(updatedColumns) => onColumnsChange?.(updatedColumns)}
          tableId={tableId}
          customFields={customFields}
          useCustomFieldsMerging={viewType === ViewType.CONTRACTS_TABLE}
        />
      )}
    </>
  );
};
export default DataTableToolbar;
